import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    type OnInit,
    computed,
    effect,
    forwardRef,
    inject,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import type { IProductDataV3 } from '@df/catalog/product/product';
import { getAbsolutePosition } from '@df/dom/fn/get-absolute-position';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ProductConfigurationBaseServiceDecorator } from '../../../../../module/product-configuration/model/base/product-configuration-base.service.decorator';
import { BreadcrumbsComponent } from '../../../../../ui/common/breadcrumbs.component';
import type { ProductDecorator } from '../../../../model/product/product.decorator';
import { ICmsOutletBlockComponent } from '../../../../outlet-block/icms-outlet-block.component';
import { PageComponentCountdownBannerComponent } from '../../countdown-banner/countdown-banner.component';
import { ProductGiftBoxComponent } from '../../gift/box/product-gift-box.component';
import { ProductLayoutAddToBasketComponent } from '../add-to-basket/product-layout-add-to-basket.component';
import { ProductLayoutConfigurableOptionsComponent } from '../configurable-options/product-layout-configurable-options.component';
import { ProductLayoutDeliveryMessagesComponent } from '../delivery/messages/product-layout-delivery-messages.component';
import { ProductLayoutComponent } from '../product-layout.component';
import type { NProductLayout } from '../product-layout.interface';
import { ProductLayoutShareComponent } from '../share/product-layout-share.component';
import { ProductLayoutSummaryComponent } from '../summary/product-layout-summary.component';
import { ProductLayoutUspBlockComponent } from '../usp-block/product-layout-usp-block.component';
import { ProductLayoutDetailsNameComponent } from './name/product-layout-details-name.component';
import { ProductBundleOptionsComponent } from '../../product-bundle-options.component';

@Component({
    selector: 'product-layout-details',
    templateUrl: './product-layout-details.component.html',
    styleUrl: './product-layout-details.component.scss',
    imports: [
        ProductLayoutAddToBasketComponent,
        BreadcrumbsComponent,
        ICmsClientModule,
        ICmsOutletBlockComponent,
        PageComponentCountdownBannerComponent,
        ProductLayoutDeliveryMessagesComponent,
        forwardRef(() => ProductGiftBoxComponent),
        ProductLayoutConfigurableOptionsComponent,
        ProductLayoutDetailsNameComponent,
        ProductLayoutShareComponent,
        ProductLayoutSummaryComponent,
        ProductLayoutUspBlockComponent,
        SizeClassDirective,
        TranslatePipe,
        ProductBundleOptionsComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductLayoutDetailsComponent extends UiComponent implements OnInit {
    readonly product = input.required<ProductDecorator<IProductDataV3>>();
    readonly content = input.required<NProductLayout.IContent>();
    readonly split = input(false);

    readonly productNameVisible = output<boolean>();

    /**
     * Gift box
     */
    readonly shouldAddGiftBoxProduct = signal(false);

    protected readonly showCountdownBanner = computed(() => this.product().inStock && !!this.product().data?.allow_countdown_baner);

    protected readonly productViewLayout = inject(ProductLayoutComponent);
    private readonly baseConfiguration = inject(ProductConfiguration).baseService as ProductConfigurationBaseServiceDecorator;
    private readonly visibilityService = inject(VisibilityService);
    private readonly docRef = inject(DOCUMENT);

    private readonly productNameRef = viewChild<ProductLayoutDetailsNameComponent>('productNameRef');

    constructor() {
        super();

        effect(() => {
            const productNameRef = this.productNameRef();

            if (productNameRef) {
                this.subscriptions.add(
                    this.visibilityService
                        .isInView(productNameRef.elementRef, {
                            threshold: 1.0
                        })
                        .subscribe(visible => {
                            const position = getAbsolutePosition(productNameRef.elementRef.nativeElement);
                            // is visible when visible is true or position y is less than the scroll top
                            const isVisible = visible || position.y <= this.docRef.documentElement.scrollTop;

                            this.productNameVisible.emit(isVisible);
                        })
                );
            }
        });
    }

    override ngOnInit(): void {
        this.subscriptions.add(
            this.baseConfiguration.additionalProducts$.subscribe(products => {
                if (!products.length) {
                    this.shouldAddGiftBoxProduct.set(false);
                    this.detectChanges();
                }
            })
        );

        super.ngOnInit();
    }

    giftBoxSelectionChangeHandle(value: boolean) {
        this.shouldAddGiftBoxProduct.set(value);

        const giftBoxProductId = this.product().data?.gift_box_product_id;

        if (value && giftBoxProductId) {
            this.baseConfiguration.additionalProducts$.next([
                {
                    id: giftBoxProductId,
                    qty: 1
                }
            ]);
        } else {
            this.baseConfiguration.additionalProducts$.next([]);
        }

        this.detectChanges();
    }
}
